import { makeObservable, override } from 'mobx';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';

import type { ReferralData } from 'chaika-frontend-types/frontend-cms';
import { ReferralStatus } from 'chaika-enums';

import { GroupedList, GroupedListParams } from '@store/abstract/GroupedList';
import { store } from '@store';

const STATUS_WEIGHT = {
    [ReferralStatus.Active]: 0,
    [ReferralStatus.Executed]: 1,
    [ReferralStatus.Cancelled]: 2,
    [ReferralStatus.Expired]: 3,
};

export interface ReferralListFilterParams {
    id?: string;
    ids?: string[];
    entryId?: string;
    query?: string;
    ehrId?: string;
    serviceId?: string;
    serviceIdFromCompositeService?: string;
    executeEntryId?: string;
    statuses?: ReferralStatus[];
    reservationExist?: boolean;
}

export class ReferralList extends GroupedList<GroupedListParams<ReferralData>, ReferralData, ReferralListFilterParams> {
    constructor(referrals: ReferralData[] = []) {
        super();
        makeObservable(this);

        this.filterParams = {
            id: undefined,
            entryId: undefined,
            statuses: [],
            reservationExist: undefined,
        };

        this.set(referrals);
    }

    public serialize(): ReferralData[] {
        return this.values;
    }

    @override
    public setFilterParams(params: Partial<ReferralListFilterParams>) {
        this.filterParams = { ...this.filterParams, ...params };
    }

    @override
    public update(id: string, value: Partial<GroupedListParams<ReferralData>>): void {
        const { ids, count, ...current } = this.getExisting(id);
        this.list.set(id, { ...current, ids, count, ...value });
    }

    protected getOrderedValues(
        params: GroupedListParams<GroupedListParams<ReferralData>>[],
    ): GroupedListParams<GroupedListParams<ReferralData>>[] {
        return orderBy(params, [({ status }) => STATUS_WEIGHT[status], 'creationTime'], ['asc', 'desc']);
    }

    protected toStringGroupParams({
        serviceId,
        startDate,
        status,
        duration,
        comment,
        executionEntryId,
        reservationExist,
        authorId,
        userId,
        organizationId,
        parentEntryId,
        service,
        medicalCaseIds = [],
        id,
    }: ReferralData): string {
        const plans = service?.plans || [];
        const isLabReferral = store.serviceStore.isServiceLaboratory(serviceId);

        return `${isLabReferral || !!service?.laboratoryService?.id ? id : ''}${
            service?.laboratoryService?.id
        }${reservationExist}${parentEntryId}${authorId}${userId}${organizationId}${serviceId}${executionEntryId}${
            executionEntryId ? ReferralStatus.Executed : status
        }${startDate}${duration}${comment}${plans.map(({ subscriptionId }) => subscriptionId).join(',')}|${[...medicalCaseIds].sort().join()}`;
    }

    protected create(params: GroupedListParams<ReferralData>): GroupedListParams<ReferralData> {
        return params;
    }

    protected filterValue(
        value: GroupedListParams<GroupedListParams<ReferralData>>,
        {
            id,
            ids: filterIds,
            statuses,
            entryId,
            ehrId,
            executeEntryId,
            serviceIdFromCompositeService,
            serviceId: filterServiceId,
            reservationExist: reservationExistFilter,
        }: ReferralListFilterParams,
    ): boolean {
        const idsFilterSet = !!id || !!filterIds ? (!!id ? new Set([id]) : new Set(filterIds)) : undefined;
        const {
            status,
            ids,
            reservationExist,
            parentEntryId,
            executionEntryId,
            tiedEntryIds = [],
            serviceId,
            compositeService,
        } = value;

        return (
            (!isNil(serviceIdFromCompositeService)
                ? !!compositeService?.compositeServices.find(it => it.id === serviceIdFromCompositeService)
                : true) &&
            (statuses && statuses.length > 0 ? statuses.includes(status) : true) &&
            (!isNil(reservationExistFilter) ? reservationExistFilter === reservationExist : true) &&
            (!isNil(idsFilterSet) ? ids.some(id => idsFilterSet.has(id)) : true) &&
            (!isNil(entryId)
                ? parentEntryId === entryId || executionEntryId === entryId || tiedEntryIds.includes(entryId)
                : true) &&
            this.filterFieldValue(value, ehrId, 'ehrId') &&
            (!isNil(executeEntryId)
                ? parentEntryId !== executeEntryId && !tiedEntryIds.includes(executeEntryId)
                : true) &&
            (!isNil(filterServiceId) ? filterServiceId === serviceId : true)
        );
    }
}
